import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { motion } from 'framer-motion';
import { containerVariants, itemVariants } from '../lib/animations';
import CategoryDetailLayout from '../components/category-detail/CategoryDetailLayout.js';
import { useChild, useChildAssessments } from '../hooks/useAssessment';
import { RecommendationsCarousel } from '../components/category-recommendations';
import { useCategoryRecommendations } from '../hooks/useCategoryRecommendations';
import { useCategorySummary } from '../hooks/useCategorySummary';
import DevelopmentStageBadge from '../components/assessment/DevelopmentStageBadge';
import CategoryDetailSkeleton from '../components/assessment/CategoryDetailSkeleton';
import {
  PROFILE_COLORS,
  PROFILE_TYPOGRAPHY,
} from '../components/assessment/constants';
import type { ChildAssessmentSummary } from '../types/assessment.types';
import { useTimeOfDay } from '../components/home-v2';
import { useContentManager } from './HomeV2Page';
import { CheckInTransitionOverlay, useCheckInTransition } from '../components/home-v2/check-in-transition';

/**
 * Format a date string as "24 October 2025"
 */
function formatAssessmentDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Calculate the "next quiz due in" text from completedAt.
 * Returns human-readable string like "2 weeks", "3 days", "overdue".
 */
function getNextQuizDueText(completedAt: string): string {
  const completedDate = new Date(completedAt);
  const dueDate = new Date(completedDate.getTime() + 14 * 24 * 60 * 60 * 1000);
  const now = new Date();
  const diffMs = dueDate.getTime() - now.getTime();

  if (diffMs <= 0) return 'overdue';

  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays >= 14) return '2 weeks';
  if (diffDays >= 7) return '1 week';
  if (diffDays === 1) return '1 day';
  return `${diffDays} days`;
}

export default function CategoryDetailPage() {
  const { track } = useAnalytics();
  const { childId, categoryId } = useParams<{
    childId: string;
    categoryId: string;
  }>();
  const navigate = useNavigate();

  const { config } = useTimeOfDay();


    const {
      //cloudRef,
      isTransitioning,
      cloudRect,
      startTransition,
      onTransitionComplete,
      // resetTransition,
    } = useCheckInTransition();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (childId && categoryId) {
      track('category_detail_view', { child_id: childId, category_id: categoryId });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId, categoryId]);

  const numericChildId = childId ? parseInt(childId, 10) : 0;
  const numericCategoryId = categoryId ? parseInt(categoryId, 10) : 0;

  // Fetch data
  const { data: child, isLoading: childLoading } = useChild(numericChildId);
  const { data: assessments, isLoading: assessmentsLoading } =
    useChildAssessments(numericChildId);

  const isLoading = childLoading || assessmentsLoading;

  // Fetch category recommendations (uses childId from URL params — v1 single-child design)
  const {
    data: recommendationsData,
    isLoading: isLoadingRecs,
    isRegenerating: isRegeneratingRecs,
  } = useCategoryRecommendations({
    childId: numericChildId || null,
    categoryId: numericCategoryId,
  });

  // Find the most recent completed assessment for this category
  const latestAssessment: ChildAssessmentSummary | undefined = assessments?.find(
    (a) => a.categoryId === numericCategoryId
  );

  const { categorySummary, isGenerating: summaryGenerating, error: summaryError } =
    useCategorySummary(latestAssessment?.id ?? null, numericChildId);

  // Determine category name and key from assessment data
  const categoryName = latestAssessment?.categoryName ?? 'Category';
  const categoryKey = latestAssessment?.categoryKey ?? '';

  // Handle back navigation
  function handleBack() {
    navigate(`/profile/${childId}`);
  }

  // Handle retake quiz
  function handleRetakeQuiz() {
    if (categoryKey) {
      navigate(`/assessment/${childId}?category=${categoryKey}`);
    }
  }


    const {
      hideHeaderAvatar,
    } = useContentManager()

  return (
    <>
    <CategoryDetailLayout
      timeOfDayConfig={config}
      activeTab="journey"
      showCloudLogo
      showAvatar={!hideHeaderAvatar}
      onCloudClick={startTransition}
      >
      {isLoading ? (
        <CategoryDetailSkeleton headerBackground={config.headerBackground} />
      ) : (
        <div>
          {/* Back Navigation + Category Title */}
          <div
            style={{
              backgroundColor: config.headerBackground,
              transition: 'background-color 0.5s ease-in-out',
            }}>
            <motion.div variants={itemVariants}>
              <button
                type="button"
                onClick={handleBack}
                className="flex items-center gap-2 py-4 bg-white px-6 w-full"
                aria-label="Back to profile"
                style={{
                  borderTopLeftRadius: 24,
                  borderTopRightRadius: 24,
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 18L9 12L15 6"
                    stroke={PROFILE_COLORS.textPrimary}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <h1
                  className={`${PROFILE_TYPOGRAPHY.pageTitle.weight} ${PROFILE_TYPOGRAPHY.pageTitle.sizeClasses} ${PROFILE_TYPOGRAPHY.pageTitle.tracking}`}
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    color: PROFILE_COLORS.textPrimary,
                  }}
                >
                  {categoryName}
                </h1>
              </button>
            </motion.div>
          </div>
          <motion.div
            className="flex flex-col gap-6 px-6 py-4 pb-8"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Stage Badge */}
            {latestAssessment?.overallStage && (
              <motion.div variants={itemVariants}>
                <DevelopmentStageBadge stage={latestAssessment.overallStage} />
              </motion.div>
            )}

            {/* Development Narrative (AI-generated) */}
            {!summaryError && (categorySummary || summaryGenerating) && (
              <motion.div variants={itemVariants}>
                {summaryGenerating && (
                  <div className="flex items-center gap-3" style={{ color: PROFILE_COLORS.textSecondary }}>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600" />
                    <p
                      className={`${PROFILE_TYPOGRAPHY.body.weight} ${PROFILE_TYPOGRAPHY.body.sizeClasses}`}
                      style={{ fontFamily: 'DM Sans, sans-serif' }}
                    >
                      Generating insights...
                    </p>
                  </div>
                )}
                {categorySummary && !summaryGenerating && (
                  <>
                    {categorySummary.split('\n\n').map((paragraph, i) => (
                      <p
                        key={i}
                        className={`${PROFILE_TYPOGRAPHY.body.weight} ${PROFILE_TYPOGRAPHY.body.sizeClasses} ${i > 0 ? 'mt-3' : ''}`}
                        style={{
                          fontFamily: 'DM Sans, sans-serif',
                          color: PROFILE_COLORS.textSecondary,
                        }}
                      >
                        {paragraph}
                      </p>
                    ))}
                  </>
                )}
              </motion.div>
            )}

            {/* Assessment Date Info */}
            {latestAssessment?.completedAt && (
              <motion.div variants={itemVariants} className="flex flex-col gap-3">
                {/* Last quiz date */}

                {
                  categorySummary && (
                    <div>
                      <p
                        className={`${PROFILE_TYPOGRAPHY.dateLabel.weight} ${PROFILE_TYPOGRAPHY.dateLabel.sizeClasses}`}
                        style={{
                          fontFamily: 'DM Sans, sans-serif',
                          color: PROFILE_COLORS.textTertiary,
                        }}
                      >
                        This assessment is based on the last quiz taken on
                      </p>
                      <p
                        className={`${PROFILE_TYPOGRAPHY.dateValue.weight} ${PROFILE_TYPOGRAPHY.dateValue.sizeClasses} ${PROFILE_TYPOGRAPHY.dateValue.tracking}`}
                        style={{
                          fontFamily: 'DM Sans, sans-serif',
                          color: PROFILE_COLORS.textPrimary,
                        }}
                      >
                        {formatAssessmentDate(latestAssessment.completedAt)}
                      </p>
                    </div>
                  )
                }

                {/* Next quiz due */}
                <div>
                  <p
                    className={`${PROFILE_TYPOGRAPHY.dateLabel.weight} ${PROFILE_TYPOGRAPHY.dateLabel.sizeClasses}`}
                    style={{
                      fontFamily: 'DM Sans, sans-serif',
                      color: PROFILE_COLORS.textTertiary,
                    }}
                  >
                    Next quiz is due in
                  </p>
                  <p
                    className={`${PROFILE_TYPOGRAPHY.dateValue.weight} ${PROFILE_TYPOGRAPHY.dateValue.sizeClasses} ${PROFILE_TYPOGRAPHY.dateValue.tracking}`}
                    style={{
                      fontFamily: 'DM Sans, sans-serif',
                      color: PROFILE_COLORS.textPrimary,
                    }}
                  >
                    {getNextQuizDueText(latestAssessment.completedAt)}
                  </p>
                </div>
              </motion.div>
            )}

            {/* Retake Quiz Button */}
            <motion.div variants={itemVariants}>
              <button
                type="button"
                onClick={handleRetakeQuiz}
                className="w-full flex items-center justify-center"
                style={{
                  height: '48px',
                  backgroundColor: PROFILE_COLORS.primaryBlue,
                  borderRadius: '12px',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                <span
                  className={`${PROFILE_TYPOGRAPHY.button.weight} ${PROFILE_TYPOGRAPHY.button.sizeClasses} ${PROFILE_TYPOGRAPHY.button.extra}`}
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    color: PROFILE_COLORS.white,
                  }}
                >
                  RETAKE QUIZ NOW
                </span>
              </button>
            </motion.div>

            {/* Recommended Activities Carousel */}
            {recommendationsData && !isLoadingRecs && (
              <RecommendationsCarousel
                activities={recommendationsData.activities}
                isRegenerating={isRegeneratingRecs}
                childName={child?.nickname}
              />
            )}
          </motion.div>
        </div>
      )}
    </CategoryDetailLayout>

          {/* Check-in transition overlay */}
          {isTransitioning && (
            <CheckInTransitionOverlay
              cloudRect={cloudRect}
              onComplete={onTransitionComplete}
            // onReplay={resetTransition}
            />
          )}

    </>
  );
}
